import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import zh from './locales/zh.json';

// Supported languages for v1 of the localization feature (English / Simplified
// Chinese). Later sprints extend the locale bundles but should not need to
// touch this list.
export const SUPPORTED_LANGUAGES = ['en', 'zh'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Keys used to persist the user's chosen language across sessions. v1 is
// localStorage-only — no backend/account sync. The customer storefront and
// the admin console each get their own key so toggling language on one
// surface never leaks into the other, even though both share a single
// i18next instance (the two surfaces are mutually exclusive React Router
// branches and are never mounted at the same time).
export const CUSTOMER_LANGUAGE_STORAGE_KEY = 'projectc_language_customer';
export const ADMIN_LANGUAGE_STORAGE_KEY = 'projectc_language_admin';

function isSupportedLanguage(value: string | null): value is SupportedLanguage {
  return (
    value !== null &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
  );
}

export function getStoredLanguage(storageKey: string): SupportedLanguage {
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (isSupportedLanguage(stored)) {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (e.g. disabled/private browsing) —
    // fall back to the default language.
  }
  return DEFAULT_LANGUAGE;
}

// Persist a language choice under the given surface-specific key. Called
// explicitly by LanguageToggle (rather than via a global 'languageChanged'
// listener) so that persistence always targets the storage key for the
// surface the toggle was clicked on.
export function persistLanguage(storageKey: string, lng: string): void {
  try {
    window.localStorage.setItem(storageKey, lng);
  } catch {
    // ignore write failures (e.g. storage disabled)
  }
}

// Resolve which surface-specific storage key applies for a given pathname —
// the admin console (/admin/*) uses its own key, everything else is treated
// as the customer storefront.
export function getStorageKeyForPath(pathname: string): string {
  return pathname.startsWith('/admin') ? ADMIN_LANGUAGE_STORAGE_KEY : CUSTOMER_LANGUAGE_STORAGE_KEY;
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    zh: { translation: zh },
  },
  lng: getStoredLanguage(getStorageKeyForPath(typeof window !== 'undefined' ? window.location.pathname : '/')),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
